import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, UserPlus, Shield } from 'lucide-react'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { listAdmins, promoteToAdmin, demoteAdmin } from '../../api/users'
import { useAuth } from '../../context/AuthContext'

export default function AdminsPage() {
  const { isSuperadmin } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [newAdminTg, setNewAdminTg] = useState('')

  const { data: admins, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: listAdmins,
    enabled: isSuperadmin,
  })

  const promoteMut = useMutation({
    mutationFn: () => promoteToAdmin(newAdminTg.trim()),
    onSuccess: (user) => {
      toast.success(`${user.full_name} назначен администратором`)
      setNewAdminTg('')
      qc.invalidateQueries({ queryKey: ['admins'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Ошибка'),
  })

  const demoteMut = useMutation({
    mutationFn: (userId: number) => demoteAdmin(userId),
    onSuccess: () => {
      toast.success('Права администратора сняты')
      qc.invalidateQueries({ queryKey: ['admins'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Ошибка'),
  })

  const backBtn = (
    <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      Назад
    </button>
  )

  if (!isSuperadmin) {
    return (
      <Layout title="Администраторы" hideNav>
        {backBtn}
        <p className="text-center text-tg-hint text-sm mt-8">Доступ только для суперадмина</p>
      </Layout>
    )
  }

  if (isLoading) return <Spinner className="h-screen" />

  return (
    <Layout title="Администраторы" hideNav>
      {backBtn}
      <div className="flex flex-col gap-4">
        {/* Stats badge */}
        <div className="card flex items-center gap-3">
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(249,115,22,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="#F97316" />
          </div>
          <div>
            <p className="text-xs text-tg-hint">Всего администраторов</p>
            <p className="text-2xl font-bold text-tg-text">{admins?.length ?? 0}</p>
          </div>
        </div>

        {/* Admins list */}
        <div className="card">
          <p className="section-title mb-3">Список администраторов</p>
          {admins && admins.length > 0 ? (
            <div className="flex flex-col">
              {admins.map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-3 border-b border-tg-hint/10 last:border-0">
                  <div
                    style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#A8A8AE' }}>
                      {a.full_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/admin/admins/${a.id}`)}>
                    <p className="text-sm font-medium text-tg-text truncate">{a.full_name}</p>
                    <p className="text-xs text-tg-hint truncate">{a.phone} · ID: {a.telegram_id}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/admin/admins/${a.id}`)}
                    style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#8A8A96' }}
                  >
                    <ChevronRight size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Снять права администратора у ${a.full_name}?`)) {
                        demoteMut.mutate(a.id)
                      }
                    }}
                    disabled={demoteMut.isPending}
                    className="text-xs px-3 py-1.5 rounded-lg disabled:opacity-40 flex-shrink-0"
                    style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}
                  >
                    Убрать
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-tg-hint py-4 text-center">Администраторов нет</p>
          )}
        </div>

        {/* Add admin */}
        <div className="card">
          <p className="section-title mb-1">Добавить администратора</p>
          <p className="text-xs text-tg-hint mb-3">Пользователь должен сначала открыть бота, чтобы появиться в системе</p>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="Telegram ID пользователя"
              value={newAdminTg}
              onChange={(e) => setNewAdminTg(e.target.value)}
              inputMode="numeric"
            />
            <button
              onClick={() => promoteMut.mutate()}
              disabled={!newAdminTg.trim() || promoteMut.isPending}
              className="disabled:opacity-40 px-4 py-3 rounded-xl font-semibold text-sm flex items-center gap-2"
              style={{ background: 'rgba(249,115,22,0.15)', color: '#F97316' }}
            >
              <UserPlus size={16} />
              {promoteMut.isPending ? '...' : 'Добавить'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}
